import "./UserList.css";
import { useState, useEffect } from "react";
import { useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import PhoneInput from "react-phone-input-2";
import { toastSuccess, toastError } from "../../Services/toaster";
import "react-phone-input-2/lib/style.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
function UserList() {
  const [tab, setTab] = useState("profile");
  const [showAddUser, setShowAddUser] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = useAuthStore((state) => state.token);
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    employee_code: "",
    first_name: "",
    last_name: "",
    dob: "",
    email_id: "",
    country_code: "+91",
    mobile_no: "",
    password: "",
    role: "",
    department: "",
    profile: null, // ✅ null not ""
    isactive: 1,
  });

  const handleChange = (e) => {
    const { name, type, files, value } = e.target;

    if (type === "file") {
      setFormData({
        ...formData,
        profile: files[0] || null,
      });
      return;
    }

    // First name / Last name - alphabets only
    if (name === "first_name" || name === "last_name") {
      if (!/^[A-Za-z\s]*$/.test(value)) {
        return;
      }
    }
    // Employee code - letters and numbers only
    if (name === "employee_code") {
      if (!/^[A-Za-z0-9]*$/.test(value)) {
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const getUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      setUsers(response.data.data);
    } catch (err) {
      console.error("Failed to load users:", err.response?.data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);
  const validateUserForm = () => {
    // Employee Code
    const employeeCode = formData.employee_code.trim();

    if (!employeeCode) {
      toastError("Employee code is required.");
      return false;
    }

    // Only letters and numbers
    if (!/^[A-Za-z0-9]+$/.test(employeeCode)) {
      toastError("Employee code can contain only letters and numbers.");
      return false;
    }

    // Role
    if (!formData.role) {
      toastError("Please select a role.");
      return false;
    }

    // First Name
    const firstName = formData.first_name.trim();

    if (!firstName) {
      toastError("First name is required.");
      return false;
    }

    if (!/^[A-Za-z]+$/.test(firstName)) {
      toastError("First name can contain only alphabets.");
      return false;
    }

    if (firstName.length < 2) {
      toastError("First name must contain at least 2 characters.");
      return false;
    }

    // Last Name
    const lastName = formData.last_name.trim();

    if (!lastName) {
      toastError("Last name is required.");
      return false;
    }

    if (!/^[A-Za-z]+$/.test(lastName)) {
      toastError("Last name can contain only alphabets.");
      return false;
    }

    if (lastName.length < 2) {
      toastError("Last name must contain at least 2 characters.");
      return false;
    }

    // DOB
    if (!formData.dob) {
      toastError("Date of birth is required.");
      return false;
    }

    const selectedDOB = new Date(formData.dob);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    if (selectedDOB > today) {
      toastError("Date of birth cannot be a future date.");
      return false;
    }

    // Age validation
    let age = today.getFullYear() - selectedDOB.getFullYear();

    const monthDifference = today.getMonth() - selectedDOB.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < selectedDOB.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      toastError("User must be at least 18 years old.");
      return false;
    }

    // Email
    const email = formData.email_id.trim();

    if (!email) {
      toastError("Email address is required.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!emailRegex.test(email)) {
      toastError("Please enter a valid email address.");
      return false;
    }

    // Mobile
    const mobile = formData.mobile_no.replace(/\D/g, "");

    if (!mobile) {
      toastError("Mobile number is required.");
      return false;
    }

    // Exactly 10 digits
    if (!/^\d{10}$/.test(mobile)) {
      toastError("Mobile number must contain exactly 10 digits.");
      return false;
    }

    // Prevent numbers like 0000000000
    if (/^(\d)\1{9}$/.test(mobile)) {
      toastError("Please enter a valid mobile number.");
      return false;
    }

    // Password
    const password = formData.password;

    if (!password) {
      toastError("Password is required.");
      return false;
    }

    // Minimum 8 characters
    if (password.length < 8) {
      toastError("Password must be at least 8 characters long.");
      return false;
    }

    // Strong password:
    // uppercase + lowercase + number + special character
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

    if (!strongPasswordRegex.test(password)) {
      toastError(
        "Password must contain uppercase, lowercase, number and special character.",
      );
      return false;
    }

    // Department
    if (!formData.department) {
      toastError("Please select a department.");
      return false;
    }

    // Profile image
    if (formData.profile instanceof File) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
      ];

      if (!allowedTypes.includes(formData.profile.type)) {
        toastError("Profile image must be JPG, JPEG, PNG or WEBP.");
        return false;
      }

      // 2 MB limit
      if (formData.profile.size > 2 * 1024 * 1024) {
        toastError("Profile image size must be less than 2 MB.");
        return false;
      }
    }

    return true;
  };
  const handleSubmit = async () => {
    if (!validateUserForm()) {
      return;
    }

    try {
      const authToken = useAuthStore.getState().token;

      const form = new FormData();

      form.append("employee_code", formData.employee_code);
      form.append("first_name", formData.first_name);
      form.append("last_name", formData.last_name);
      form.append("dob", formData.dob);
      form.append("email_id", formData.email_id);
      form.append("country_code", formData.country_code);
      form.append("mobile_no", formData.mobile_no);
      form.append("password", formData.password);
      form.append("role", formData.role);
      form.append(
        "department",
        formData.department ? Number(formData.department) : "",
      );
      form.append("isactive", formData.isactive);

      if (formData.profile instanceof File) {
        form.append("profile", formData.profile);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/add`,
        form,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/json",
          },
        },
      );

      toastSuccess("User created successfully");
      setShowAddUser(false);
      getUsers();
    } catch (err) {
      console.log("ERROR RESPONSE:", err.response?.data);

      // Clean generic message instead of backend dummy message
      toastError(
        err.response?.data?.message ||
          "Unable to create user. Please check the details and try again.",
      );
    }
  };
  const handleEditSubmit = async () => {
    try {
      const token = useAuthStore.getState().token;

      const form = new FormData();

      form.append("employee_code", formData.employee_code);
      form.append("first_name", formData.first_name);
      form.append("last_name", formData.last_name);

      form.append("dob", formData.dob || "");
      form.append("email_id", formData.email_id);
      form.append("country_code", formData.country_code);
      form.append("mobile_no", formData.mobile_no);
      form.append("role", formData.role);

      form.append(
        "department",
        formData.department ? Number(formData.department) : "",
      );

      form.append("isactive", formData.isactive);

      if (formData.profile instanceof File) {
        form.append("profile", formData.profile);
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update/${selectedUser.id}`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );

      console.log("UPDATE SUCCESS", response.data);
      toastSuccess("User updated successfully");
      setShowAddUser(false);
      getUsers();
    } catch (err) {
      console.log("UPDATE ERROR:", err.response?.data);
      toastError(err.response?.data?.detail || "Update failed");
    }
  };
  const getUserById = async (id) => {
    try {
      const token = useAuthStore.getState().token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );

      const user = res.data.data;
      setFormData({
        employee_code: user.employee_code || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        dob: user.dob || "",
        email_id: user.email_id || "",
        country_code: user.country_code || "+91",
        mobile_no: user.mobile_no || "",
        password: user.password || "",
        role: user.role || "",
        department: user.department ? String(user.department) : "",
        profile: user.profileurl || null, // <-- change here
        isactive: user.isactive ?? 1,
      });

      setSelectedUser(user);
      setIsEditMode(true);
      setShowAddUser(true);
    } catch (err) {
      console.log("GET USER ERROR:", err.response?.data);
      toastError("Failed to load user details");
    }
  };
  const toggleUserStatus = async (user) => {
    try {
      const token = useAuthStore.getState().token;

      const newStatus = user.isactive === 1 ? 0 : 1;

      const form = new FormData();

      form.append("employee_code", user.employee_code || "");
      form.append("first_name", user.first_name || "");
      form.append("last_name", user.last_name || "");
      form.append("dob", user.dob || ""); // IMPORTANT
      form.append("email_id", user.email_id || "");
      form.append("country_code", user.country_code || "");
      form.append("mobile_no", user.mobile_no || "");
      form.append("role", user.role || "");
      form.append("department", user.department ? Number(user.department) : "");
      form.append("isactive", newStatus);

      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update/${user.id}`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isactive: newStatus } : u)),
      );

      toastSuccess(
        newStatus === 1
          ? "User activated successfully"
          : "User deactivated successfully",
      );
    } catch (err) {
      console.log("STATUS UPDATE ERROR:", err.response?.data);
      toastError("Failed to update status");
    }
  };
  if (loading) {
    return <h2>Loading Users...</h2>;
  }
  const filteredUsers = users.filter((user) => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return true;

    const fullName =
      `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();

    return (
      fullName.includes(search) ||
      user.employee_code?.toLowerCase().includes(search) ||
      user.role?.toLowerCase().includes(search) ||
      String(user.department || "")
        .toLowerCase()
        .includes(search) ||
      user.email_id?.toLowerCase().includes(search) ||
      user.mobile_no?.toLowerCase().includes(search)
    );
  });
  return (
    <>
      {showAddUser ? (
        <div className="add-user-page">
          {/* Header */}
          <div className="add-user-header">
            <div className="back-btn">
              <button
                className="back-back-btn"
                onClick={() => setShowAddUser(false)}
              >
                <img src={BackIcon} alt="Back" className="back-icon" />
              </button>
            </div>

            <div className="header-title">
              <h2>{isEditMode ? "Edit User" : "Add New User"}</h2>
            </div>
          </div>

          {/* Tabs */}
          <div className="user-tabs">
            <button
              className={tab === "profile" ? "tab active" : "tab"}
              onClick={() => setTab("profile")}
            >
              Profile
            </button>

            <button
              className={tab === "permission" ? "tab active" : "tab"}
              onClick={() => setTab("permission")}
            >
              Permissions
            </button>
          </div>

          <div className="user-form-card">
            {/* PROFILE */}
            {tab === "profile" && (
              <>
                <div className="form-grid">
                  {/* Employee Code */}
                  <div className="form-group">
                    <label>Employee Code *</label>
                    <input
                      name="employee_code"
                      value={formData.employee_code}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                    >
                      <option value="">Select Role</option>
                      <option value="Admin">Admin</option>
                      <option value="Staff">Staff</option>
                    </select>
                  </div>

                  {/* First Name */}
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      maxLength={50}
                    />
                  </div>

                  {/* Last Name */}
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      maxLength={50}
                    />
                  </div>

                  {/* DOB */}
                  <div className="form-group">
                    <label>Date of Birth *</label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email_id"
                      value={formData.email_id}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Mobile Number</label>

                    <PhoneInput
                      country={"in"}
                      value={formData.country_code + formData.mobile_no}
                      countryCodeEditable={false}
                      disableDropdown={false}
                      inputProps={{
                        name: "mobile_no",
                        required: true,
                      }}
                      onChange={(value, country) => {
                        setFormData({
                          ...formData,
                          country_code: "+" + country.dialCode,
                          mobile_no: value.slice(country.dialCode.length),
                        });
                      }}
                      inputStyle={{
                        width: "100%",
                        height: "40px",
                      }}
                    />
                  </div>

                  {/* Password */}
                  <div className="form-group">
                    <label>Password *</label>

                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter Password"
                        className="password-input"
                      />

                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  {/* Department */}
                  <div className="form-group">
                    <label>Department *</label>
                    <select
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                    >
                      <option value="">Select Department</option>
                      <option value="1">Admin</option>
                      <option value="2">Operations</option>
                      <option value="3">Finance</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label>Status</label>
                    <div className="status-toggle">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={formData.isactive === 1}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              isactive: e.target.checked ? 1 : 0,
                            })
                          }
                        />
                        <span className="slider"></span>
                      </label>
                      <span>
                        {formData.isactive === 1 ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  {/* Profile Image — real file input */}
                  <div className="form-group">
                    <label>Profile Image</label>
                    <div
                      className="upload-box"
                      onClick={() =>
                        document.getElementById("profile-upload").click()
                      }
                    >
                      <i className="ti ti-upload"></i>
                      {formData.profile instanceof File
                        ? formData.profile.name
                        : formData.profile
                          ? formData.profile.split("/").pop()
                          : "Click to Upload"}
                      <input
                        id="profile-upload"
                        type="file"
                        name="profile"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* PERMISSION */}
            {tab === "permission" && (
              <>
                <h3>User Permissions</h3>
                <p>Select permissions for this user.</p>

                <div className="permission-list">
                  <label>
                    <input type="checkbox" /> Dashboard
                  </label>
                  <label>
                    <input type="checkbox" /> Users
                  </label>
                  <label>
                    <input type="checkbox" /> Events
                  </label>
                  <label>
                    <input type="checkbox" /> Finance
                  </label>
                  <label>
                    <input type="checkbox" /> Inventory
                  </label>
                  <label>
                    <input type="checkbox" /> Reports
                  </label>
                  <label>
                    <input type="checkbox" /> Settings
                  </label>
                </div>
              </>
            )}

            <div className="form-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowAddUser(false)}
              >
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={isEditMode ? handleEditSubmit : handleSubmit}
              >
                {isEditMode ? "Update " : "Save "}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="user-list-page">
          {/* Header */}
          <div className="page-header">
            <div className="page-title">
              <h2>User List</h2>
              <p>Manage your Club OS user accounts</p>
            </div>

            <div className="page-actions">
              <div className="gm-search-box">
                <span className="gm-search-icon">🔍</span>

                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                className="btn-primary"
                onClick={() => {
                  setIsEditMode(false);
                  setSelectedUser(null);
                  setFormData({
                    employee_code: "",
                    first_name: "",
                    last_name: "",
                    dob: "",
                    email_id: "",
                    country_code: "+91",
                    mobile_no: "",
                    password: "",
                    role: "",
                    department: "",
                    profile: null,
                    isactive: 1,
                  });
                  setShowAddUser(true);
                }}
              >
                <i className="ti ti-user-plus"></i>
                <span>Add New User</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-card">
            <div className="table-responsive">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>#</th>

                    <th>Full Name</th>
                    <th>Employee Code</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th>Email</th>
                    <th>Mobile Number</th>
                    <th>Date of Birth</th>
                    <th>Active</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={`${user.first_name || ""} ${user.last_name || ""}`}
                        >
                          {user.first_name || ""} {user.last_name || ""}
                        </div>
                      </td>

                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={user.employee_code || ""}
                        >
                          {user.employee_code || "-"}
                        </div>
                      </td>

                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={user.role || ""}
                        >
                          {user.role || "-"}
                        </div>
                      </td>

                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={user.department || ""}
                        >
                          {user.department || "-"}
                        </div>
                      </td>

                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={user.email_id || ""}
                        >
                          {user.email_id || "-"}
                        </div>
                      </td>

                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={`${user.country_code || ""} ${user.mobile_no || ""}`}
                        >
                          {user.country_code} {user.mobile_no}
                        </div>
                      </td>

                      <td>
                        <div
                          className="user-cell-text"
                          data-tooltip={user.dob || ""}
                        >
                          {user.dob || "-"}
                        </div>
                      </td>
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={user.isactive === 1}
                            onChange={() => toggleUserStatus(user)}
                          />
                          <span className="slider"></span>
                        </label>
                      </td>
                      <td className="action-cell">
                        <button
                          className="edit-btn"
                          onClick={() => getUserById(user.id)}
                        >
                          <img src={editIcon} alt="Edit" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserList;
